import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import theme from 'theme/theme';
import utils from 'helpers/utils';

const StyledBreadcrumbWrapper = styled.div`
    display: flex;
    margin-bottom: 20px;
    height: 12px;

    div {
        color: ${ theme.palette.gray };
        font-size: 12px;
        line-height: 1;
        text-transform: capitalize;
        user-select: none;

        a {
            text-decoration: none;
            color: ${ theme.palette.gray };

            &:hover {
                color: ${ theme.palette.black };
            }
        }

        &:after {
            content: '>';
            padding: 0 10px;
            color: ${ theme.palette.gray };
            user-select: none;
            cursor: default;
        }

        &:last-child {
            color: ${ theme.palette.black };

            &:after {
                content: none;
            }
        }
    }
`;

const DEFAULT_EXCLUDED_ENDPOINTS = [
    'show',
];

const DEFAULT_INCLUDED_ENDPOINTS = [
    'create',
    'edit',
];

/**
 * Generates breadcrumbs array from path and payload.
 * Returns without show while on show page.
 *
 * @todo implement different payload data-structure support nested payloads with same identifiers.
 *
 * @param {Object} payload
 * @param {Object} props
 *
 * @returns {Object[]} Returns a breadcrumbs array from payload and path.
 */
export const generateBreadcrumbsFromPath = (payload, props) => {
    const { translate } = props;
    const paths = window.location.pathname
        .split('/')
        .filter((endpoint) => {
            return endpoint;
        }); // Filters out any empty strings after split

    const breadcrumbs = [];

    utils.each(paths, (endpoint, index) => {
        if (!endpoint || DEFAULT_EXCLUDED_ENDPOINTS.includes(endpoint)) {
            return; // Skips adding show to breadcrumbs
        }

        let label = endpoint;
        let link = null;

        if (DEFAULT_INCLUDED_ENDPOINTS.includes(endpoint)) {
            label = props.translate(`actions.${ endpoint }`);
        }

        /**
         * Checks first if property with key of endpoint is an object, then checks if it is next to last endpoint.
         * If so doesn't include the link
         *
         * e.g. contains link '/products/{id}/'
         * e.g. excludes link '/products/{id}/show'
         */
        if (utils.isObject(payload[endpoint]) && index === paths.length - 2) {
            const entity = payload[endpoint];

            label = entity.name || entity.title;
        } else if (utils.isObject(payload[endpoint])) {
            const entity = payload[endpoint];

            label = entity.name || entity.title;
            link = `/${ [].concat(paths).splice(0, index + 1).join('/') }/show`;
        } else if (utils.isString(payload[endpoint])) {
            label = payload[endpoint];

            if (!index) {
                link = `/${ [].concat(paths).splice(0, index + 1).join('/') }`;
            }
        }

        breadcrumbs.push({
            label,
            link,
        });
    });

    /**
     * Adds edit if last endpoint references an object from the payload.
     * Example edit route '/products/id/'.
     */
    if (utils.isObject(payload[paths[paths.length - 1]])) {
        breadcrumbs.push({
            label: translate('actions.edit'),
        });
    }

    return breadcrumbs;
};

const Breadcrumbs = (props) => {
    const {
        breadcrumbs,
    } = props;

    return (
        <StyledBreadcrumbWrapper>
            {
                breadcrumbs.map((element) => {
                    if (element.link) {
                        return (
                            <div key={ element.label }>
                                <Link to={ element.link }>{ element.label }</Link>
                            </div>
                        );
                    }

                    return <div key={ element.label }>{ element.label }</div>;
                })
            }
        </StyledBreadcrumbWrapper>
    );
};

Breadcrumbs.propTypes = {
    breadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string,
            ]).isRequired,
            link: PropTypes.string,
        }),
    ),
};

export default Breadcrumbs;



// WEBPACK FOOTER //
// ./src/components/layout/Layout/Breadcrumbs.js